import { useCallback, useMemo, useState } from 'react'

/**
 * Generic multi-select state for list rows. Keys are stored as strings so number
 * and string ids interoperate. Pure UI state — no fetching here.
 *
 *   const sel = useBulkSelect()
 *   sel.toggle(row.id)            // check / uncheck one
 *   sel.isSelected(row.id)        // boolean
 *   sel.toggleAll(allIds)         // select all / clear (header checkbox)
 *   sel.selectedIds               // string[] for the API call
 *   sel.count                     // number selected
 *   sel.clear()                   // after an action completes
 */
export interface BulkSelect {
  selectedIds: string[]
  count: number
  isSelected: (id: string | number) => boolean
  toggle: (id: string | number) => void
  toggleAll: (ids: Array<string | number>) => void
  allSelected: (ids: Array<string | number>) => boolean
  clear: () => void
}

export function useBulkSelect(): BulkSelect {
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const isSelected = useCallback((id: string | number) => selected.has(String(id)), [selected])

  const toggle = useCallback((id: string | number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      const key = String(id)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const allSelected = useCallback(
    (ids: Array<string | number>) => ids.length > 0 && ids.every((id) => selected.has(String(id))),
    [selected]
  )

  const toggleAll = useCallback(
    (ids: Array<string | number>) => {
      setSelected((prev) => {
        const keys = ids.map(String)
        const everyOn = keys.length > 0 && keys.every((k) => prev.has(k))
        // If everything visible is already selected, clear; otherwise select all visible.
        return everyOn ? new Set() : new Set(keys)
      })
    },
    []
  )

  const clear = useCallback(() => setSelected(new Set()), [])

  const selectedIds = useMemo(() => Array.from(selected), [selected])

  return { selectedIds, count: selected.size, isSelected, toggle, toggleAll, allSelected, clear }
}
